import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';
import { toast } from 'react-toastify';
import config from '../config';
import ImageCropperModal from './common/ImageCropperModal';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SOURCE_SIZE = 10 * 1024 * 1024; // 10MB Source limit

// initialImages and initialLimit come from the venue the page has already loaded, so the
// gallery can open straight away. Parents should set key={venueId}: these are read only once.
const VenueGalleryManager = ({ venueId, initialImages, initialLimit }) => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { setActiveTabId } = useApplicationContext();
    
    const hasInitialImages = Array.isArray(initialImages);
    const [images, setImages] = useState(hasInitialImages ? initialImages : []);
    // max stays null while the photos are known but the plan limit is not
    const [limits, setLimits] = useState({
        max: hasInitialImages ? (initialLimit ?? null) : 0,
        current: hasInitialImages ? initialImages.length : 0
    });
    const [loading, setLoading] = useState(!hasInitialImages);
    const [needsInitialFetch] = useState(!hasInitialImages || initialLimit == null);
    const [uploading, setUploading] = useState(false);
    const [stagedFiles, setStagedFiles] = useState([]); // [{ id, file, preview, name, size }]
    const [deletingId, setDeletingId] = useState(null);
    const [savingOrder, setSavingOrder] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);

    // Optional manual cropping state for a specific staged file
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        fileId: null,
        fixedAspectRatio: 3/2,
        initialAspectRatio: 3/2,
        minWidth: 400
    });

    // Cleanup object previews on unmount
    useEffect(() => {
        return () => {
            stagedFiles.forEach(item => {
                if (item.preview && item.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(item.preview);
                }
            });
        };
    }, [stagedFiles]);

    // Fetch gallery and limits
    const fetchGallery = useCallback(async () => {
        try {
            // Try v2 first, fallback to v1
            let response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}/gallery`);
            if (!response.ok) {
                response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery`);
            }
            const data = await response.json();
            
            if (response.ok) {
                const imageList = Array.isArray(data) ? data : (data.images || []);
                const limitValue = !Array.isArray(data) && data.limit !== undefined ? data.limit : 0;
                
                setImages(imageList);
                setLimits({
                    max: limitValue,
                    current: imageList.length
                });
            }
        } catch (error) {
            console.error('Error fetching gallery:', error);
        } finally {
            setLoading(false);
        }
    }, [auth, venueId]);

    useEffect(() => {
        if (venueId && needsInitialFetch) {
            fetchGallery();
        }
    }, [venueId, fetchGallery, needsInitialFetch]);

    const getRemainingSlots = () => {
        return Math.max(0, limits.max - limits.current);
    };

    // Handle multiple file selection
    const handleFileSelect = (e) => {
        const rawFiles = Array.from(e.target.files || []);
        if (!rawFiles.length) return;

        const availableSlots = getRemainingSlots() - stagedFiles.length;
        if (availableSlots <= 0) {
            toast.error(`You have reached the maximum allowed images for your current plan.`);
            e.target.value = '';
            return;
        }

        const validNewFiles = [];
        const filesToProcess = rawFiles.slice(0, availableSlots);

        if (rawFiles.length > availableSlots) {
            toast.warning(`Only ${availableSlots} more image(s) can be added based on your quota.`);
        }

        for (const file of filesToProcess) {
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                toast.error(`${file.name}: Only JPG, PNG, and WebP are allowed`);
                continue;
            }
            if (file.size > MAX_SOURCE_SIZE) {
                toast.error(`${file.name}: File size must be less than 10MB`);
                continue;
            }

            const objectUrl = URL.createObjectURL(file);
            validNewFiles.push({
                id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                file: file,
                preview: objectUrl,
                name: file.name,
                size: (file.size / (1024 * 1024)).toFixed(2)
            });
        }

        if (validNewFiles.length > 0) {
            setStagedFiles(prev => [...prev, ...validNewFiles]);
        }

        e.target.value = '';
    };

    // Remove staged file
    const removeStagedFile = (id) => {
        setStagedFiles(prev => {
            const item = prev.find(f => f.id === id);
            if (item?.preview?.startsWith('blob:')) {
                URL.revokeObjectURL(item.preview);
            }
            return prev.filter(f => f.id !== id);
        });
    };

    // Open cropper for a specific staged file
    const openCropperForFile = (stagedItem) => {
        setCropperState({
            show: true,
            imageSrc: stagedItem.preview,
            fileId: stagedItem.id,
            fixedAspectRatio: 3/2,
            initialAspectRatio: 3/2,
            minWidth: 400
        });
    };

    const handleCropperHide = () => {
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null, fileId: null }));
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        if (cropperState.fileId) {
            setStagedFiles(prev => prev.map(item => {
                if (item.id === cropperState.fileId) {
                    if (item.preview && item.preview.startsWith('blob:')) {
                        URL.revokeObjectURL(item.preview);
                    }
                    return {
                        ...item,
                        file: croppedFile,
                        preview: previewUrl,
                        name: croppedFile.name || item.name,
                        size: (croppedFile.size / (1024 * 1024)).toFixed(2)
                    };
                }
                return item;
            }));
        }
        handleCropperHide();
    };

    // Upload all staged images
    const handleUploadAll = async () => {
        if (!stagedFiles.length) return;

        setUploading(true);

        try {
            const formData = new FormData();
            stagedFiles.forEach(item => {
                formData.append('images', item.file);
            });

            // Post to v2 with fallback
            let response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}/gallery`, {
                method: 'POST',
                body: formData,
                headers: {}
            });

            if (!response.ok) {
                response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery`, {
                    method: 'POST',
                    body: formData,
                    headers: {}
                });
            }

            const data = await response.json();

            if (response.ok) {
                const count = data.uploaded?.length || stagedFiles.length;
                toast.success(`${count} image(s) uploaded successfully!`);
                
                // Cleanup staged previews
                stagedFiles.forEach(item => {
                    if (item.preview?.startsWith('blob:')) {
                        URL.revokeObjectURL(item.preview);
                    }
                });
                setStagedFiles([]);
                fetchGallery();
            } else {
                toast.error(data.message || 'Failed to upload images');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('An error occurred during upload');
        } finally {
            setUploading(false);
        }
    };

    // Persist reordered images to backend
    const persistOrder = async (orderedImages) => {
        setSavingOrder(true);
        try {
            const orderPayload = orderedImages.map((img, idx) => ({
                id: img.id,
                display_order: idx
            }));

            let response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}/gallery/reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order: orderedImages.map(img => img.id), images: orderPayload })
            });

            if (!response.ok) {
                response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery/reorder`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ order: orderedImages.map(img => img.id), images: orderPayload })
                });
            }

            if (!response.ok) {
                const err = await response.json();
                toast.error(err.message || 'Could not save gallery order');
                fetchGallery();
            }
        } catch (err) {
            console.error('Reorder error:', err);
            toast.error('Failed to save gallery order');
        } finally {
            setSavingOrder(false);
        }
    };

    // Move image position by offset (-1 for left, +1 for right)
    const moveImage = (index, offset) => {
        const targetIndex = index + offset;
        if (targetIndex < 0 || targetIndex >= images.length) return;

        const newImages = [...images];
        const [movedItem] = newImages.splice(index, 1);
        newImages.splice(targetIndex, 0, movedItem);

        setImages(newImages);
        persistOrder(newImages);
    };

    // HTML5 Drag and Drop handlers
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }

        const newImages = [...images];
        const [movedItem] = newImages.splice(draggedIndex, 1);
        newImages.splice(dropIndex, 0, movedItem);

        setImages(newImages);
        setDraggedIndex(null);
        persistOrder(newImages);
    };

    // Delete image
    const handleDelete = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this photo from the gallery?')) return;

        setDeletingId(imageId);
        try {
            let response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}/gallery/${imageId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery/${imageId}`, {
                    method: 'DELETE'
                });
            }

            if (response.ok) {
                toast.success('Image removed from gallery');
                fetchGallery();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to delete image');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete image');
        } finally {
            setDeletingId(null);
        }
    };

    // Set as primary (cover)
    const handleSetPrimary = async (imageId) => {
        try {
            let response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}/gallery/${imageId}/primary`, {
                method: 'PUT'
            });

            if (!response.ok) {
                response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/gallery/${imageId}/primary`, {
                    method: 'PUT'
                });
            }

            if (response.ok) {
                toast.success('Primary cover image updated');
                fetchGallery();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to update primary image');
            }
        } catch (error) {
            console.error('Error setting primary:', error);
            toast.error('Failed to update primary image');
        }
    };

    const handleUpgrade = (e) => {
        if (e) e.preventDefault();
        setActiveTabId('my-plan');
        navigate('/applications');
    };

    if (loading) {
        return (
            <div className="hs-card p-40 text-center bg-white border radius-16 shadow-none" style={{ minHeight: '480px' }}>
                <div className="spinner-border text-primary" role="status" />
                <p className="text-secondary-light text-sm mt-12 mb-0">Loading venue gallery...</p>
            </div>
        );
    }

    const remainingSlots = getRemainingSlots();

    return (
        <div className="d-flex flex-column gap-20">
            {/* Header / Stats Card */}
            <div className="hs-card bg-white border radius-16 shadow-none p-20 px-24">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-48-px h-48-px rounded-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                            <Icon icon="solar:gallery-wide-bold" className="text-2xl" />
                        </div>
                        <div>
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="text-lg fw-bold text-dark mb-0">Venue Photo Gallery</h5>
                                {savingOrder && (
                                    <span className="badge bg-warning-50 text-warning-700 border border-warning-200 text-2xs fw-semibold px-2 py-1 radius-4">
                                        Saving order...
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-secondary-light mb-0">
                                Upload multiple photos, drag or use arrows to arrange order. The first image (#1) serves as your primary cover photo.
                            </p>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="hs-badge hs-badge-info">
                            {limits.max === null ? `${limits.current} Photos` : limits.max === 0 ? 'Not included in plan' : `${limits.current} / ${limits.max} photos`}
                        </span>
                    </div>
                </div>
            </div>

            {/* Upload & Staging Card */}
            {limits.max === null ? null : limits.max === 0 ? (
                <div className="hs-card bg-white border radius-16 p-24 text-center">
                    <Icon icon="solar:lock-keyhole-bold" className="text-4xl text-neutral-400 mb-8" />
                    <h6 className="text-base fw-bold text-dark mb-4">Gallery Feature Not Available</h6>
                    <p className="text-xs text-secondary-light mb-16">
                        Your current subscription plan does not allow gallery uploads.
                    </p>
                    <button type="button" onClick={handleUpgrade} className="hs-btn-primary hs-btn-sm">
                        Upgrade Your Plan
                    </button>
                </div>
            ) : remainingSlots > 0 || stagedFiles.length > 0 ? (
                <div className="hs-card bg-white border radius-16 shadow-none p-24">
                    {/* Drag-and-drop file selector */}
                    <div className="border-2 border-dashed border-primary-200 radius-12 p-32 text-center bg-primary-50 bg-opacity-25 hover-bg-primary-50 transition-2 cursor-pointer position-relative">
                        <input
                            type="file"
                            id="gallery-multi-upload"
                            multiple
                            accept=".jpg,.jpeg,.png,.webp"
                            onChange={handleFileSelect}
                            className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                            title="Drop multiple images or click to browse"
                        />
                        <div className="d-flex flex-column align-items-center justify-content-center pointer-events-none">
                            <div className="w-56-px h-56-px rounded-circle bg-white shadow-xs text-primary-600 d-flex align-items-center justify-content-center mb-12">
                                <Icon icon="solar:cloud-upload-bold" className="text-3xl" />
                            </div>
                            <h6 className="text-sm fw-bold text-dark mb-4">
                                Click or drag multiple images to upload
                            </h6>
                            <p className="text-xs text-secondary-light mb-0">
                                Select up to {remainingSlots} image(s) at once • JPG, PNG, WebP • Max 10MB each
                            </p>
                        </div>
                    </div>

                    {/* Staging Area for Selected Files */}
                    {stagedFiles.length > 0 && (
                        <div className="mt-20 pt-20 border-top">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-16">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="badge bg-primary-600 text-white rounded-pill px-8 py-2 text-xs fw-bold">
                                        {stagedFiles.length}
                                    </span>
                                    <span className="text-sm fw-bold text-dark">
                                        Selected for Upload
                                    </span>
                                    <span className="text-xs text-secondary-light">
                                        (Review thumbnails below, adjust crop if desired, then click Upload)
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm radius-8 px-12 py-6 text-xs"
                                        onClick={() => {
                                            stagedFiles.forEach(item => {
                                                if (item.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview);
                                            });
                                            setStagedFiles([]);
                                        }}
                                        disabled={uploading}
                                    >
                                        Clear All
                                    </button>
                                    <button
                                        type="button"
                                        className="hs-btn-primary hs-btn-sm d-inline-flex align-items-center gap-2"
                                        onClick={handleUploadAll}
                                        disabled={uploading}
                                    >
                                        {uploading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" />
                                                <span>Uploading {stagedFiles.length} Photos...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="solar:upload-track-2-bold" className="text-base" />
                                                <span>Upload {stagedFiles.length} Image(s) Now</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="row g-16">
                                {stagedFiles.map((item, index) => (
                                    <div key={item.id} className="col-6 col-sm-4 col-md-3 col-lg-2">
                                        <div className="position-relative radius-12 overflow-hidden border border-neutral-200 shadow-xs bg-white">
                                            <div style={{ height: '110px' }} className="position-relative bg-neutral-100">
                                                <img
                                                    src={item.preview}
                                                    alt={item.name}
                                                    className="w-100 h-100 object-fit-cover"
                                                />
                                                <div className="position-absolute top-0 end-0 m-6 d-flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => openCropperForFile(item)}
                                                        className="w-24-px h-24-px rounded-circle bg-white text-dark shadow-2xs border-0 d-flex align-items-center justify-content-center p-0"
                                                        title="Crop / Adjust Ratio"
                                                    >
                                                        <Icon icon="solar:crop-bold" className="text-xs text-primary-600" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeStagedFile(item.id)}
                                                        className="w-24-px h-24-px rounded-circle bg-white text-danger shadow-2xs border-0 d-flex align-items-center justify-content-center p-0"
                                                        title="Remove from batch"
                                                    >
                                                        <Icon icon="solar:close-circle-bold" className="text-xs text-danger-600" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="p-8 bg-white border-top">
                                                <p className="text-2xs fw-semibold text-truncate mb-2 text-dark" title={item.name}>
                                                    {item.name}
                                                </p>
                                                <span className="text-3xs text-secondary-light">
                                                    {item.size} MB
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="alert alert-warning mb-0 radius-12 d-flex align-items-center justify-content-between p-16">
                    <div className="d-flex align-items-center gap-2">
                        <Icon icon="solar:danger-triangle-bold" className="text-xl text-warning-700" />
                        <span className="text-xs fw-semibold text-warning-800">
                            You have reached your gallery limit of {limits.max} photos. Delete photos or upgrade your plan to upload more.
                        </span>
                    </div>
                    <button type="button" onClick={handleUpgrade} className="hs-btn-primary hs-btn-sm py-4 px-12 text-xs">
                        Upgrade
                    </button>
                </div>
            )}

            {/* Gallery Order & Grid Card */}
            <div className="hs-card bg-white border radius-16 shadow-none p-24">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-20 pb-16 border-bottom">
                    <div>
                        <h6 className="text-sm fw-bold text-dark mb-4">
                            Active Gallery Media ({images.length})
                        </h6>
                        <span className="text-xs text-secondary-light">
                            Use the left/right arrow buttons or drag cards to reorder. Changes save automatically.
                        </span>
                    </div>

                    {images.length > 1 && (
                        <div className="d-flex align-items-center gap-2 text-xs text-secondary-light bg-neutral-50 px-12 py-6 radius-8 border">
                            <Icon icon="solar:reorder-bold" className="text-sm text-primary-600" />
                            <span>Drag cards or click arrows to arrange</span>
                        </div>
                    )}
                </div>

                {images.length > 0 ? (
                    <div className="row g-16">
                        {images.map((image, index) => {
                            const isFirst = index === 0;
                            const isLast = index === images.length - 1;
                            const url = (image.thumbnail_url || image.image_url)?.startsWith('http')
                                ? (image.thumbnail_url || image.image_url)
                                : `${config.API_BASE_URL}/${image.thumbnail_url || image.image_url}`.replace(/([^:]\/)\/+/g, "$1");

                            return (
                                <div
                                    key={image.id}
                                    className="col-12 col-sm-6 col-md-4 col-lg-3"
                                    draggable={true}
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={(e) => handleDrop(e, index)}
                                >
                                    <div
                                        className={`radius-12 border overflow-hidden bg-white shadow-2xs transition-2 position-relative ${
                                            draggedIndex === index ? 'opacity-50 border-primary' : 'border-neutral-200'
                                        }`}
                                        style={{ cursor: 'grab' }}
                                    >
                                        {/* Image Display */}
                                        <div className="position-relative" style={{ height: '170px' }}>
                                            <img
                                                src={url}
                                                alt={image.caption || `Gallery photo #${index + 1}`}
                                                className="w-100 h-100 object-fit-cover"
                                                onError={(e) => {
                                                    if (image.image_url) {
                                                        const fallback = image.image_url.startsWith('http')
                                                            ? image.image_url
                                                            : `${config.API_BASE_URL}/${image.image_url}`.replace(/([^:]\/)\/+/g, "$1");
                                                        if (e.target.src !== fallback) {
                                                            e.target.src = fallback;
                                                            return;
                                                        }
                                                    }
                                                    e.target.src = '/assets/images/logo.png';
                                                }}
                                            />

                                            {/* Order Chip (Top Left) */}
                                            <div className="position-absolute top-0 start-0 m-8">
                                                <span className={`badge ${isFirst ? 'bg-primary-600 text-white' : 'bg-dark bg-opacity-75 text-white'} px-8 py-3 text-xs fw-bold radius-6 shadow-xs d-inline-flex align-items-center gap-1`}>
                                                    {isFirst ? (
                                                        <>
                                                            <Icon icon="solar:star-bold" className="text-warning-400" />
                                                            <span>#1 Cover</span>
                                                        </>
                                                    ) : (
                                                        <span>#{index + 1}</span>
                                                    )}
                                                </span>
                                            </div>

                                            {/* Actions (Top Right) */}
                                            <div className="position-absolute top-0 end-0 m-8 d-flex gap-1">
                                                {!isFirst && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSetPrimary(image.id)}
                                                        className="w-28-px h-28-px rounded-circle bg-white text-dark shadow-2xs border-0 d-flex align-items-center justify-content-center p-0"
                                                        title="Make Primary Cover Photo"
                                                    >
                                                        <Icon icon="solar:star-linear" className="text-sm text-warning-600" />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(image.id)}
                                                    disabled={deletingId === image.id}
                                                    className="w-28-px h-28-px rounded-circle bg-white text-danger shadow-2xs border-0 d-flex align-items-center justify-content-center p-0"
                                                    title="Delete Image"
                                                >
                                                    {deletingId === image.id ? (
                                                        <span className="spinner-border spinner-border-sm text-danger" />
                                                    ) : (
                                                        <Icon icon="solar:trash-bin-trash-bold" className="text-sm text-danger-600" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Reorder Toolbar Footer */}
                                        <div className="p-8 px-12 bg-neutral-50 border-top d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-1 text-secondary-light text-2xs">
                                                <Icon icon="solar:reorder-bold" className="text-xs" />
                                                <span>Position {index + 1} of {images.length}</span>
                                            </div>

                                            {/* Left / Right Swap Arrows */}
                                            <div className="d-flex align-items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => moveImage(index, -1)}
                                                    disabled={isFirst || savingOrder}
                                                    className={`btn btn-sm p-4 w-28-px h-28-px d-flex align-items-center justify-content-center radius-6 ${
                                                        isFirst ? 'btn-light opacity-25 text-neutral-400' : 'btn-white shadow-2xs text-dark'
                                                    }`}
                                                    title={isFirst ? 'Already at start' : 'Move Left'}
                                                >
                                                    <Icon icon="solar:alt-arrow-left-bold" className="text-sm" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => moveImage(index, 1)}
                                                    disabled={isLast || savingOrder}
                                                    className={`btn btn-sm p-4 w-28-px h-28-px d-flex align-items-center justify-content-center radius-6 ${
                                                        isLast ? 'btn-light opacity-25 text-neutral-400' : 'btn-white shadow-2xs text-dark'
                                                    }`}
                                                    title={isLast ? 'Already at end' : 'Move Right'}
                                                >
                                                    <Icon icon="solar:alt-arrow-right-bold" className="text-sm" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-48 bg-neutral-50 radius-12 border border-dashed border-neutral-300">
                        <Icon icon="solar:gallery-wide-bold" className="text-5xl text-neutral-300 mb-12" />
                        <h6 className="text-sm fw-bold text-dark mb-4">No Photos in Gallery Yet</h6>
                        <p className="text-xs text-secondary-light mb-0">
                            Upload photos above to showcase your venue to guests.
                        </p>
                    </div>
                )}
            </div>

            {/* Image Cropper Modal (Only if user opts to adjust crop) */}
            {cropperState.show && (
                <ImageCropperModal
                    show={cropperState.show}
                    onHide={handleCropperHide}
                    imageSrc={cropperState.imageSrc}
                    onCropComplete={handleCropComplete}
                    fixedAspectRatio={cropperState.fixedAspectRatio}
                    initialAspectRatio={cropperState.initialAspectRatio}
                    minWidth={cropperState.minWidth}
                />
            )}
        </div>
    );
};

export default VenueGalleryManager;
